import ApiWithLogging from '../utils/apiWithLogging';

// ข้อมูลผู้ใช้
export interface UserInfo {
  id: number;
  username: string;
  full_name?: string;
}

// ประเภทข้อมูลสินค้า
export interface Product {
  id: number;
  code: string;
  name: string;
  description: string;
  monthlyRentalRate: number;
  depositAmount: number;
  minimumRentalDays: number;
  unit: string;
  isActive: boolean;
  syncedFromFlow: boolean;
  flowaccountId?: string;
  flowaccountSyncDate?: string;
  flowaccountData?: any; // ข้อมูลทั้งหมดที่ได้จาก Flow Account API
  created_at: string;
  updated_at?: string;
  created_by?: UserInfo; // ผู้สร้าง
  updated_by?: UserInfo; // ผู้แก้ไขล่าสุด
  // Backward compatibility
  createdBy?: string;
  updatedBy?: string;
}

// ข้อมูลสำหรับการสร้างหรือแก้ไขสินค้า
export interface ProductForm {
  code: string;
  name: string;
  description?: string;
  monthlyRentalRate: number;
  depositAmount?: number;
  minimumRentalDays?: number;
  unit?: string;
  isActive?: boolean;
}


// พารามิเตอร์สำหรับการดึงข้อมูลสินค้า
export interface GetProductsParams {
  page?: number;
  limit?: number;
  search?: string;
  is_active?: boolean;
}

// การตอบกลับจากการดึงข้อมูลสินค้า
export interface GetProductsResponse {
  items: Product[];
  total: number;
  page: number;
  limit: number;
}

/**
 * KD3 S2108 (BUG-S2108-003): GET /products/ คืน timestamp เป็น camelCase (createdAt/updatedAt)
 * ขณะที่ GET/PUT รายตัวคืน snake_case — หน้าสินค้าอ่าน `updated_at` จึงได้ undefined
 * → ไม่ส่ง If-Match เลย แก้พร้อมกัน 2 แท็บแล้วทับข้อมูลคนอื่นเงียบ ๆ (+ คอลัมน์ "อัพเดทล่าสุด" ว่าง)
 * เติมคีย์ snake ให้เสมอ ผู้ใช้ทุกจุดจะได้ค่าเดียวกันไม่ว่า backend จะคืนแบบไหน
 */
export const withSnakeTimestamps = <T extends Record<string, any>>(p: T): T => {
  if (!p || typeof p !== 'object') return p;
  const out: any = { ...p };
  if (out.created_at == null && out.createdAt != null) out.created_at = out.createdAt;
  if (out.updated_at == null && out.updatedAt != null) out.updated_at = out.updatedAt;
  return out;
};

// บริการจัดการข้อมูลสินค้า
const productService = {
  // ดึงรายการสินค้าทั้งหมด
  async getProducts(params: GetProductsParams = {}): Promise<GetProductsResponse> {
    try {
      // ใช้ค่าเริ่มต้นถ้าไม่มีการระบุ
      const queryParams = {
        page: params.page || 1,
        limit: params.limit || 10,
        search: params.search,
        is_active: params.is_active
      };
      
      console.log('Fetching products with params:', queryParams);
      
      // ส่ง request ไปยัง API
      const response = await ApiWithLogging.get<GetProductsResponse>('/products/', 
        { params: queryParams },
        {
          service: 'productService',
          action: 'view_products_list',
          entityType: 'product'
        }
      );
      
      console.log('Fetched products response:', response);
      if (response && Array.isArray((response as any).items)) {
        return { ...response, items: response.items.map(withSnakeTimestamps) };
      }
      return response;
    } catch (error: any) {
      console.error('Error in getProducts service:', error);
      
      // Log more detailed error information if available
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      
      throw error;
    }
  },

  // ดึงข้อมูลสินค้าตาม id
  async getProduct(id: number): Promise<Product> {
    try {
      const response = await ApiWithLogging.get<Product>(`/products/${id}`, {},
        {
          service: 'productService',
          action: 'view_product_detail',
          entityType: 'product',
          entityId: id
        }
      );
      return withSnakeTimestamps(response);
    } catch (error) {
      console.error('Error in getProduct service:', error);
      throw error;
    }
  },

  // สร้างสินค้าใหม่
  async createProduct(data: ProductForm): Promise<Product> {
    try {
      // แปลงข้อมูลจาก frontend format เป็น backend format
      const productData = {
        code: data.code,
        name: data.name,
        description: data.description,
        monthly_rental_rate: data.monthlyRentalRate,
        deposit_amount: data.depositAmount,
        minimum_rental_days: data.minimumRentalDays,
        unit: data.unit,
        is_active: data.isActive !== undefined ? data.isActive : true
      };
      
      console.log('Creating product with data:', productData);
      
      const response = await ApiWithLogging.post<Product>('/products/', productData, {},
        {
          service: 'productService',
          action: 'create_product',
          entityType: 'product',
          details: {
            code: data.code,
            name: data.name
          }
        }
      );
      return response;
    } catch (error: any) {
      console.error('Error in createProduct service:', error);
      
      // Log more detailed error information if available
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      
      throw error;
    }
  },

  // แก้ไขข้อมูลสินค้า
  async updateProduct(id: number, data: Partial<ProductForm>, ifMatch?: string): Promise<Product> {
    try {
      // แปลงข้อมูลจาก frontend format เป็น backend format
      const productData = {
        name: data.name,
        description: data.description,
        monthly_rental_rate: data.monthlyRentalRate,
        deposit_amount: data.depositAmount,
        minimum_rental_days: data.minimumRentalDays,
        unit: data.unit,
        is_active: data.isActive
      };
      
      console.log(`Updating product ID ${id} with data:`, productData);
      
      const response = await ApiWithLogging.put<Product>(`/products/${id}`, productData, ifMatch ? { headers: { 'If-Match': ifMatch } } : {},
        {
          service: 'productService',
          action: 'update_product',
          entityType: 'product',
          entityId: id,
          details: {
            updated_fields: Object.keys(data)
          }
        }
      );
      return response;
    } catch (error) {
      console.error('Error in updateProduct service:', error);
      throw error;
    }
  },

  // ลบสินค้า
  async deleteProduct(id: number): Promise<{success: boolean; message: string}> {
    try {
      console.log(`Deleting product with ID: ${id}`);
      
      const response = await ApiWithLogging.delete<{success: boolean; message: string; error?: any}>(`/products/${id}`, {},
        {
          service: 'productService',
          action: 'delete_product',
          entityType: 'product',
          entityId: id
        }
      );
      
      // ตรวจสอบว่าการลบสำเร็จหรือไม่
      if (response && response.success === false) {
        const errorMessage = response.error?.user_message || response.error?.message || 'ไม่สามารถลบสินค้าได้';
        throw new Error(errorMessage);
      }
      
      console.log(`✅ Product ${id} deleted successfully:`, response.message);
      return response;
    } catch (error) {
      console.error('Error in deleteProduct service:', error);
      throw error;
    }
  },


  // Sync สินค้าจาก Flow Account
  async syncFromFlowAccount(): Promise<{ message: string; count: number }> {
    try {
      const response = await ApiWithLogging.post<{ message: string; count: number }>('/sync/products', {}, {},
        {
          service: 'productService',
          action: 'sync_products_flow_account',
          entityType: 'product'
        }
      );
      return response;
    } catch (error) {
      console.error('Error syncing products with Flow Account:', error);
      throw error;
    }
  },
  // ส่งออกข้อมูลสินค้าเป็น Excel
  // ส่งออก Excel — ต้องส่งตัวกรองที่หน้าจอใช้อยู่ไปด้วย ไม่งั้นผู้ใช้ค้นหา/กรองแล้วกดส่งออก
  // จะได้ไฟล์ "ทั้งระบบ" โดยไม่มีอะไรเตือน (GAP-EXPORT-FILTER)
  // ชุดพารามิเตอร์ต้องตรงกับที่ list query ส่ง และตรงกับ signature ของ endpoint
  async exportToExcel(params?: { search?: string; is_active?: boolean }): Promise<Blob> {
    const query: Record<string, any> = {};
    if (params?.search) query.search = params.search;
    if (params?.is_active !== undefined) query.is_active = params.is_active;

    const response = await ApiWithLogging.get('/products/export/excel',
      {
        responseType: 'blob',
        params: query
      },
      {
        service: 'productService',
        action: 'export_products_excel',
        entityType: 'product'
      }
    );
    return response;
  }
};

export default productService;